import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import API, { extractErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ShieldAlert, KeyRound, Smartphone } from 'lucide-react';

/**
 * Break-glass admin login.
 *
 * Sealed-under-glass path: the *only* way to do a password-based login when
 * AUTH_MODE=sso. Two steps:
 *
 *   1. email + password    → POST /auth/break-glass/start
 *      Server sets a short-lived signed challenge cookie. If the user has
 *      not enrolled TOTP yet, the response includes the otpauth URI and
 *      the manual secret so we can render an enrollment QR.
 *
 *   2. 6-digit TOTP code   → POST /auth/break-glass/verify
 *      Server validates the challenge cookie and the code, then sets the
 *      regular auth_token + csrf_token cookies — same shape as /auth/login,
 *      so the rest of the app needs no special-casing.
 *
 * Every successful login (and every rate-limit trip) fires
 * BREAK_GLASS_WEBHOOK_URL server-side. Operators are notified in real time.
 */
export default function BreakGlassPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep]           = useState('credentials'); // 'credentials' | 'totp'
  const [enrollment, setEnrollment] = useState(null);          // { qr_uri, manual_secret } | null
  const [totpDisabled, setTotpDisabled] = useState(false);

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode]         = useState('');
  const [loading, setLoading]   = useState(false);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post('/auth/break-glass/start', { email, password });
      setTotpDisabled(!!res.data.totp_disabled);
      if (res.data.step === 'needs_enrollment') {
        setEnrollment({ qr_uri: res.data.qr_uri, manual_secret: res.data.manual_secret });
      } else {
        setEnrollment(null);
      }
      setStep('totp');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Sign-in failed'));
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post('/auth/break-glass/verify', { totp_code: code });
      // The server has set auth_token + csrf_token cookies. Hydrate the
      // AuthContext directly so we don't bounce through the login page.
      setUser(res.data.user);
      if (res.data.user.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Invalid code'));
      // Don't reset the password — they only need to retype the code.
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    try { await API.post('/auth/break-glass/cancel'); } catch { /* ignore */ }
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-5">
        <div className="text-center space-y-1">
          <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center mx-auto">
            <ShieldAlert size={22} className="text-rose-700" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Break-glass admin sign-in</h1>
          <p className="text-xs text-slate-500">
            Local-credential login for emergencies. Use SSO when possible — every successful
            sign-in here triggers an alert to your security team.
          </p>
        </div>

        {step === 'credentials' && (
          <form onSubmit={submitCredentials} className="space-y-3">
            <div>
              <Label htmlFor="bg-email" className="text-xs font-medium">Email address</Label>
              <Input
                id="bg-email" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                className="mt-1 h-9 text-sm"
                placeholder="admin@company.com"
                autoComplete="username"
                required
                data-testid="bg-email-input"
              />
            </div>
            <div>
              <Label htmlFor="bg-password" className="text-xs font-medium">Password</Label>
              <Input
                id="bg-password" type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                className="mt-1 h-9 text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                data-testid="bg-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-9 text-sm"
                    data-testid="bg-credentials-submit">
              {loading ? 'Verifying…' : 'Continue'}
            </Button>
            <button type="button" onClick={() => navigate('/login')}
                    className="block w-full text-center text-[11px] text-slate-400 hover:text-slate-600">
              ← Back to the normal sign-in page
            </button>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={submitTotp} className="space-y-4">
            {totpDisabled && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                <strong>Dev mode:</strong> TOTP is disabled (BREAK_GLASS_DISABLE_TOTP=true).
                Any 6-digit code will work — this configuration is rejected in production.
              </div>
            )}

            {enrollment && (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                  <Smartphone size={14} /> First-time enrollment
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Scan this code with your authenticator app (Authy, 1Password,
                  Google Authenticator, etc.), then type the 6-digit code it
                  shows. You only do this once — future sign-ins skip straight
                  to the code step.
                </p>
                <div className="flex justify-center bg-white rounded-md p-3">
                  <QRCodeSVG value={enrollment.qr_uri} size={148} includeMargin={false} />
                </div>
                <div className="text-[10px] text-slate-400">
                  Can't scan? Enter this secret manually:
                  <div className="mt-1 font-mono text-[11px] text-slate-700 break-all select-all">
                    {enrollment.manual_secret}
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="bg-code" className="text-xs font-medium flex items-center gap-1">
                <KeyRound size={12} /> 6-digit code
              </Label>
              <Input
                id="bg-code" type="text" inputMode="numeric" pattern="[0-9]*"
                maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1 h-10 text-lg font-mono tracking-[0.5em] text-center"
                placeholder="······"
                autoFocus
                autoComplete="one-time-code"
                required
                data-testid="bg-totp-input"
              />
            </div>
            <Button type="submit" disabled={loading || code.length !== 6}
                    className="w-full h-9 text-sm" data-testid="bg-verify-submit">
              {loading ? 'Verifying…' : (enrollment ? 'Confirm enrollment & sign in' : 'Sign in')}
            </Button>
            <button type="button" onClick={cancel}
                    className="block w-full text-center text-[11px] text-slate-400 hover:text-slate-600">
              Cancel
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
